import React, { useState, useEffect } from 'react';
import { PublicHeader } from '../../components/public/PublicHeader';
import { PublicFooter } from '../../components/public/PublicFooter';
import { INITIAL_MARKETING_PROJECTS } from '../../data/initialCmsData';
import { MarketingProjectItem } from '../../types';
import { getMarketingProjects } from '../../lib/cmsService';
import { 
  Megaphone, Sparkles, TrendingUp, CheckCircle2, ArrowRight, 
  Globe, Newspaper, Users, Tv, Share2, Award, Clapperboard,
  ShieldCheck, MessageSquare
} from 'lucide-react';

export const MarketingPage: React.FC = () => {
  const [projects, setProjects] = useState<MarketingProjectItem[]>(INITIAL_MARKETING_PROJECTS);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;
    async function load() {
      try {
        setLoading(true);
        const data = await getMarketingProjects();
        if (isMounted && data && data.length > 0) {
          setProjects(data.filter((p) => p.status === 'PUBLISHED'));
        }
      } catch (err) {
        console.warn('Error loading marketing projects:', err);
      } finally {
        if (isMounted) setLoading(false);
      }
    }
    load();
    return () => {
      isMounted = false;
    };
  }, []);

  const servicesList = [
    {
      icon: Newspaper,
      title: 'Theatrical PR & Media Relations',
      desc: 'National and regional press conferences, editorial feature placements, electronic media coverage, and red carpet media management.',
    },
    {
      icon: Award,
      title: 'Film Festival & Awards Campaigns',
      desc: 'Targeted festival entry curation, jury preview management, digital press kits, and comprehensive awards circuit publicity.',
    },
    {
      icon: Megaphone,
      title: 'Civic & Institutional Public Awareness',
      desc: 'Large-scale governmental and institutional messaging campaigns combining narrative short films with multi-district reach (e.g. Police 112).',
    },
    {
      icon: Share2,
      title: 'Digital & Influencer Syndication',
      desc: 'Social amplification, video trailer trending rollouts, digital PR syndication via premier networks like Khushi Advertising and Hungama.',
    },
    {
      icon: Tv,
      title: 'Theatrical Key Art & Trailers',
      desc: 'Poster key art strategy, cinema lobby hoardings, teaser cuts, and high-conversion commercial trailers for multi-theater launch.',
    },
    {
      icon: Users,
      title: 'Concerts & Touring PR',
      desc: 'Press coordination and brand sponsorships for live musical tours, celebrity galas, and regional arena spectacles.',
    },
  ];

  return (
    <div className="min-h-screen bg-white text-zinc-900 font-sans selection:bg-[#D97706] selection:text-white">
      <PublicHeader />

      {/* Hero Banner */}
      <section className="pt-32 pb-20 bg-gradient-to-b from-[#F8F9FA] to-white border-b border-zinc-200 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto space-y-6">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-amber-500/10 border border-amber-500/30 text-[#B45309] font-mono text-xs font-bold tracking-widest uppercase">
            <Megaphone className="w-3.5 h-3.5 text-[#D97706]" />
            PR & ENTERTAINMENT COMMUNICATIONS DIVISION
          </div>

          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6">
            <div className="space-y-3 max-w-3xl">
              <h1 className="text-4xl sm:text-6xl font-black font-serif-cinematic tracking-tight uppercase text-zinc-900 leading-none">
                PR & MARKETING
              </h1>
              <p className="text-zinc-600 text-base sm:text-lg font-light leading-relaxed">
                Strategic entertainment public relations, theatrical marketing, digital amplification, and high-impact civic campaigns that connect narratives with audiences.
              </p>
            </div>

            {/* Quick Actions */}
            <div className="flex flex-wrap gap-2 text-xs font-mono">
              <a
                href="/films"
                className="px-3 py-2 rounded-xl bg-zinc-100 hover:bg-zinc-200 text-zinc-800 border border-zinc-200 transition flex items-center gap-1.5"
              >
                <Clapperboard className="w-3.5 h-3.5 text-[#D97706]" />
                <span>Feature Slate</span>
              </a>
              <a
                href="/events"
                className="px-3 py-2 rounded-xl bg-zinc-100 hover:bg-zinc-200 text-zinc-800 border border-zinc-200 transition flex items-center gap-1.5"
              >
                <Sparkles className="w-3.5 h-3.5 text-[#D97706]" />
                <span>Premieres & Events</span>
              </a>
              <a
                href="/contact"
                className="px-4 py-2 rounded-xl bg-[#D97706] hover:bg-[#B45309] text-white font-bold transition flex items-center gap-1.5 shadow-md shadow-amber-600/20"
              >
                <span>Launch Campaign</span>
                <ArrowRight className="w-3.5 h-3.5" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto space-y-12">
        <div className="border-b border-zinc-200 pb-6">
          <span className="text-[#B45309] text-xs font-mono font-bold tracking-widest uppercase block mb-2">
            01 // CAPABILITIES MATRIX
          </span>
          <h2 className="text-3xl font-black font-serif-cinematic text-zinc-900 uppercase">
            COMPREHENSIVE PR & MARKETING SERVICES
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {servicesList.map((srv, idx) => {
            const Icon = srv.icon;
            return (
              <div
                key={idx}
                className="p-8 rounded-2xl bg-[#F8F9FA] border border-zinc-200 hover:border-[#D97706] hover:shadow-xl transition duration-300 space-y-4 group"
              >
                <div className="w-12 h-12 rounded-xl bg-amber-500/10 border border-amber-500/30 text-[#D97706] flex items-center justify-center group-hover:scale-110 transition-transform">
                  <Icon className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-bold font-serif-cinematic text-zinc-900 group-hover:text-[#D97706] transition">
                  {srv.title}
                </h3>
                <p className="text-xs text-zinc-600 font-sans leading-relaxed">
                  {srv.desc}
                </p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Highlighted Case Studies */}
      <section className="py-20 bg-zinc-50 border-t border-zinc-200/80 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto space-y-12">
          <div className="flex flex-col md:flex-row md:items-end justify-between border-b border-zinc-200 pb-6 gap-4">
            <div>
              <span className="text-[#B45309] text-xs font-mono font-bold tracking-widest uppercase block mb-2">
                02 // SELECTED CAMPAIGNS & CASE STUDIES
              </span>
              <h2 className="text-3xl sm:text-4xl font-black font-serif-cinematic text-zinc-900 uppercase">
                FEATURED PR DELIVERABLES
              </h2>
            </div>
            <p className="text-xs font-mono text-zinc-500 max-w-md">
              Real campaigns executed for theatrical features, public safety authorities, and international festival rollouts.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {projects.map((proj) => (
              <div
                key={proj.id}
                className="bg-white border border-zinc-200 rounded-2xl p-6 sm:p-8 flex flex-col justify-between hover:border-[#D97706] hover:shadow-xl transition space-y-6"
              >
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="px-2.5 py-1 rounded bg-amber-500/10 text-[#B45309] border border-amber-500/30 font-mono text-[10px] font-bold uppercase tracking-wider">
                      {proj.category || 'ENTERTAINMENT PR'}
                    </span>
                    <span className="text-xs font-mono text-zinc-400 font-bold">CASE STUDY</span>
                  </div>

                  <h3 className="text-xl font-bold font-serif-cinematic text-zinc-900">
                    {proj.title}
                  </h3>

                  <p className="text-xs text-zinc-600 font-sans leading-relaxed">
                    {proj.description}
                  </p>

                  {proj.client && (
                    <div className="text-xs font-mono pt-2 border-t border-zinc-100">
                      <span className="text-zinc-500">Client / Authority: </span>
                      <strong className="text-zinc-800">{proj.client}</strong>
                    </div>
                  )}

                  {proj.services && proj.services.length > 0 && (
                    <div className="space-y-1.5 pt-2">
                      <span className="text-[10px] font-mono text-zinc-400 uppercase tracking-wider block">
                        Deliverables:
                      </span>
                      <div className="flex flex-wrap gap-1.5">
                        {proj.services.map((s, i) => (
                          <span
                            key={i}
                            className="text-[10px] font-mono px-2 py-0.5 rounded bg-zinc-100 text-zinc-700 border border-zinc-200"
                          >
                            {s}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {proj.results && (
                  <div className="p-4 rounded-xl bg-amber-500/10 border border-amber-500/20 text-xs font-mono space-y-1">
                    <span className="text-[10px] text-[#B45309] font-bold uppercase flex items-center gap-1">
                      <TrendingUp className="w-3.5 h-3.5" /> Outcome & Impact:
                    </span>
                    <p className="text-zinc-800 font-sans text-xs">{proj.results}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Alliance Network */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto text-center space-y-8">
        <div className="max-w-3xl mx-auto space-y-4">
          <span className="text-xs font-mono text-[#B45309] font-bold uppercase tracking-widest block">
            03 // MULTIPLATFORM SYNDICATION
          </span>
          <h2 className="text-3xl font-black font-serif-cinematic text-zinc-900 uppercase">
            POWERED BY PREMIER MEDIA NETWORKS
          </h2>
          <p className="text-sm text-zinc-600 font-sans leading-relaxed">
            Stargaze Media coordinates with top cinema advertising consortiums including Khushi Advertising and Hungama Media, ensuring cinema screens, digital streaming, and print media synchronize around every launch.
          </p>
        </div>

        <div className="pt-6">
          <a
            href="/contact"
            className="inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-[#D97706] hover:bg-[#B45309] text-white font-mono font-bold text-xs uppercase tracking-widest transition shadow-lg shadow-amber-600/25"
          >
            DISCUSS A PR CAMPAIGN <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
};
